import React from 'react';
import { GraduationCap, CheckCircle2, Clock, Zap, Target, TrendingUp, Award, ChevronRight } from 'lucide-react';

const MODULES = [
    { id: 'goals', title: 'Goals & Plans', desc: 'Define and align business goals across all timeframes', status: 'completed', progress: 100, icon: Target, color: 'text-emerald-600', bg: 'bg-emerald-100' },
    { id: 'skills', title: 'Education, Skills & Traits', desc: 'Continuous learning and skill gap analysis', status: 'in-progress', progress: 65, icon: GraduationCap, color: 'text-blue-600', bg: 'bg-blue-100' },
    { id: 'wellness', title: 'Business Wellness', desc: 'Operational health monitoring and burnout prevention', status: 'in-progress', progress: 40, icon: Zap, color: 'text-amber-600', bg: 'bg-amber-100' },
    { id: 'domains', title: '14 Operational Domains', desc: 'Execute across all core business functions', status: 'pending', progress: 25, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-100' },
    { id: 'obstacles', title: 'Obstacle Overcoming', desc: 'Identify, analyze, and resolve business blockers', status: 'pending', progress: 10, icon: Award, color: 'text-rose-600', bg: 'bg-rose-100' },
    { id: 'performance', title: 'Performance Tracking', desc: 'KPI monitoring and success metric validation', status: 'pending', progress: 0, icon: Target, color: 'text-indigo-600', bg: 'bg-indigo-100' },
    { id: 'guidance', title: 'Guidance & Oversight', desc: 'Strategic direction and long-term vision', status: 'pending', progress: 0, icon: Award, color: 'text-slate-600', bg: 'bg-slate-100' },
];

const ProgressIntelligence: React.FC = () => {
    const overall = Math.round(MODULES.reduce((s, m) => s + m.progress, 0) / MODULES.length);
    const completed = MODULES.filter(m => m.status === 'completed').length;
    const inProgress = MODULES.filter(m => m.status === 'in-progress').length;

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-white p-6 rounded-[2rem] border border-slate-100 col-span-2">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="font-black text-slate-800 uppercase tracking-tight">Overall Progress</h3>
                        <span className="text-3xl font-black text-blue-600">{overall}%</span>
                    </div>
                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full transition-all duration-1000" style={{ width: `${overall}%` }} />
                    </div>
                    <div className="flex gap-4 mt-4 text-xs font-bold text-slate-500">
                        <span className="flex items-center gap-1"><CheckCircle2 size={14} className="text-emerald-500" /> {completed} Complete</span>
                        <span className="flex items-center gap-1"><Clock size={14} className="text-amber-500" /> {inProgress} In Progress</span>
                        <span className="flex items-center gap-1"><Target size={14} className="text-slate-400" /> {MODULES.length - completed - inProgress} Pending</span>
                    </div>
                </div>
                <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-6 rounded-[2rem] text-white flex flex-col justify-center">
                    <div className="text-3xl font-black">{completed}/{MODULES.length}</div>
                    <p className="text-emerald-100 text-sm font-medium">Modules Completed</p>
                </div>
                <div className="bg-gradient-to-br from-blue-600 to-indigo-700 p-6 rounded-[2rem] text-white flex flex-col justify-center">
                    <div className="text-3xl font-black">{overall}%</div>
                    <p className="text-blue-100 text-sm font-medium">Journey Completion</p>
                </div>
            </div>

            <div className="space-y-3">
                {MODULES.map(m => (
                    <div key={m.id} className="bg-white p-5 rounded-2xl border border-slate-100 flex items-center gap-4 hover:border-blue-200 transition-all cursor-pointer">
                        <div className={`p-3 rounded-xl ${m.bg}`}><m.icon size={20} className={m.color} /></div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <h4 className="font-bold text-slate-800 text-sm">{m.title}</h4>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase ${
                                    m.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                                    m.status === 'in-progress' ? 'bg-blue-50 text-blue-600' : 'bg-slate-50 text-slate-400'}`}>
                                    {m.status}
                                </span>
                            </div>
                            <p className="text-xs text-slate-400">{m.desc}</p>
                        </div>
                        <div className="w-24">
                            <div className="flex items-center justify-between text-xs font-bold mb-1">
                                <span className="text-slate-500">{m.progress}%</span>
                            </div>
                            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full transition-all ${
                                    m.status === 'completed' ? 'bg-emerald-500' : 'bg-blue-500'}`}
                                    style={{ width: `${m.progress}%` }} />
                            </div>
                        </div>
                        <ChevronRight size={16} className="text-slate-300" />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProgressIntelligence;
